/**
 * Bio-feature color helpers. The pure color MATH — luminance, contrast, hex↔rgb,
 * brightness — now lives in `@ps/functions/utils/color`; this module keeps only
 * the bio-specific pieces layered on those shared primitives: the theme
 * color-value resolver, the bright-background darkener, the named-color-aware
 * hex normalizer, and the opacity / shade-&-border helpers.
 */
import {
  hexToRgb,
  parseColorToRgb,
  perceivedLuminance,
  rgbToHex,
} from '@ps/functions/utils/color';
import type { popstore } from '@ps/types';

import { popStoreColorNormalized } from './styles';

export function simpleColorFromColorValue(
  theme: popstore.editor.ThemeConfig,
  value: popstore.Color | undefined,
): string | undefined {
  const color = popStoreColorNormalized(theme, value);
  if (color == null || color === 'none') return undefined;
  if (Array.isArray(color)) return color[0];
  return color;
}

/**
 * 1:1 port of legacy `utils/imageColorUtils.ts:adjustColorForBackground`.
 * Darkens overly-bright colors by 30% so they still work as a card
 * background. Lighter colors pass through unchanged.
 */
export function adjustColorForBackground(color: string): string {
  if (!color || !color.startsWith('#') || color.length !== 7) return color;
  const [red, green, blue] = hexToRgb(color);
  if (perceivedLuminance(red, green, blue) > 0.7) {
    const darken = (channel: number) => Math.max(0, Math.round(channel * 0.7));
    return rgbToHex(darken(red), darken(green), darken(blue));
  }
  return color;
}

/**
 * 1:1 port of legacy `flows/bio/components/ColorPickerField.tsx:convertToHex`,
 * adapted for SSR — uses a tiny RGB / named-color parser instead of
 * the DOM-based approach so it works server-side.
 *
 * Accepts `#xxx`, `#xxxxxx`, `rgb(r,g,b)`, `rgba(r,g,b,a)`, and the
 * 16 CSS named colors that legacy actually receives. Falls back to
 * the input string for anything else (matches legacy's behavior of
 * returning the original on unknown input).
 */
const kNamedColors: Record<string, string> = {
  black: '#000000',
  white: '#ffffff',
  red: '#ff0000',
  green: '#008000',
  blue: '#0000ff',
  yellow: '#ffff00',
  cyan: '#00ffff',
  magenta: '#ff00ff',
  gray: '#808080',
  grey: '#808080',
  silver: '#c0c0c0',
  maroon: '#800000',
  olive: '#808000',
  purple: '#800080',
  teal: '#008080',
  navy: '#000080',
};

export function convertToHex(color: string | undefined): string {
  if (!color) return '#000000';
  const c = color.trim().toLowerCase();
  if (c.startsWith('#')) {
    if (c.length === 4) {
      // #rgb → #rrggbb
      return `#${c[1]}${c[1]}${c[2]}${c[2]}${c[3]}${c[3]}`;
    }
    return c.length === 7 ? c : color;
  }
  if (c.startsWith('rgb')) {
    const rgb = parseColorToRgb(c);
    return rgb ? rgbToHex(rgb[0], rgb[1], rgb[2]) : color;
  }
  return kNamedColors[c] ?? color;
}

/**
 * 1:1 port of legacy `utils/imageColorUtils.ts:colorWithOpacity`.
 *
 * Returns the given color as an `rgba(...)` string with the specified opacity
 * (0–1). Supports `#rgb`, `#rrggbb`, and `rgb()`/`rgba()` strings; falls back to
 * black for unrecognised input.
 */
export function colorWithOpacity(color: string, opacity: number): string {
  const [red, green, blue] = parseColorToRgb(color) ?? [0, 0, 0];
  return `rgba(${red},${green},${blue},${opacity})`;
}

/**
 * 1:1 port of legacy `utils/imageColorUtils.ts:getShadeAndBorder`.
 *
 * Given a text color (i.e. the dominant foreground color of a surface), return
 * a subtle background-shade + border pair that stays legible against it. Bright
 * text → dark shade/border; dark text → light shade/border. Used by
 * StandaloneLeadsButton to color the inline form's inputs against the creator's
 * chosen button background.
 */
export function getShadeAndBorder(textColor?: string): {
  shade: string;
  border: string;
} {
  if (!textColor) {
    return {
      shade: 'rgba(0,0,0,0.05)',
      border: 'rgba(0,0,0,0.2)',
    };
  }

  const [red, green, blue] = parseColorToRgb(textColor) ?? [0, 0, 0];
  const brightness = (red * 299 + green * 587 + blue * 114) / 1000;
  const bright = brightness > 128;

  return {
    shade: bright ? 'rgba(0,0,0,0.1)' : 'rgba(255,255,255,0.1)',
    border: bright ? 'rgba(0,0,0,0.2)' : 'rgba(255,255,255,0.2)',
  };
}
